import { AsyncPipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import {
  AbstractControl,
  FormsModule,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormControl,
  ValidationErrors,
  ValidatorFn,
  Validators
} from '@angular/forms';
import { ModalController } from '@ionic/angular/standalone';
import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonInput,
  IonItem,
  IonLabel,
  IonRow,
  IonSpinner,
  IonTitle,
  IonToolbar
} from '@ionic/angular/standalone';
import { ComponentStore } from '@ngrx/component-store';
import { Observable, Subscription } from 'rxjs';
import { first } from 'rxjs/operators';
import { CreateIntentResponse } from 'src/app/services/payment.service';
import { ModalBackButtonComponent } from '../modal-back-button/modal-back-button.component';

export interface EditPaymentAmountModalState {
  working: boolean;
  intentError: string;
}
@Component({
  selector: 'app-edit-payment-amount-modal',
  templateUrl: './edit-payment-amount-modal.component.html',
  styleUrls: ['./edit-payment-amount-modal.component.scss'],
  providers: [ComponentStore],
  imports: [
    ModalBackButtonComponent,
    FormsModule,
    ReactiveFormsModule,
    AsyncPipe,
    IonHeader,
    IonToolbar,
    IonButtons,
    IonTitle,
    IonContent,
    IonRow,
    IonItem,
    IonLabel,
    IonInput,
    IonButton,
    IonSpinner
  ]
})
export class EditPaymentAmountModalComponent implements OnInit, OnDestroy {
  @Input() paymentTitle: string;
  @Input() balance: number;
  @Input() minimumAmount: number;
  @Input() intentRequest: (amount: number) => Observable<CreateIntentResponse>;

  @ViewChild('amountInput') amountInput: IonInput;

  form = this.fb.group({
    toPay: [null, [Validators.required, Validators.pattern(/^[0-9]+(\.[0-9]{0,2})?$/)]]
  });
  readonly setWorking = this.componentStore.updater((state, value: boolean) => ({ ...state, working: value }));
  readonly setIntentError = this.componentStore.updater((state, value: string) => ({ ...state, intentError: value }));
  readonly vm$ = this.componentStore.select(state => state);
  valueChangeSubscription: Subscription;
  constructor(
    private readonly componentStore: ComponentStore<EditPaymentAmountModalState>,
    private modalCtrl: ModalController,
    private fb: UntypedFormBuilder
  ) {
    // set defaults
    this.componentStore.setState({
      working: false,
      intentError: null
    });
  }

  ngOnInit(): void {
    this.toPay.addValidators([Validators.max(this.balance / 100.0), this.RemainingAmountValidator(this.balance)]);
    this.valueChangeSubscription = this.form.valueChanges.subscribe(() => this.setIntentError(null));
  }

  ngOnDestroy(): void {
    if (this.valueChangeSubscription) {
      this.valueChangeSubscription.unsubscribe();
    }
  }

  ionViewDidEnter(): void {
    setTimeout(() => {
      if (this.amountInput) {
        this.amountInput.setFocus();
      }
    }, 0);
  }

  onDismiss(): void {
    this.modalCtrl.dismiss();
  }

  onSubmit(): void {
    if (!this.form.valid) return;
    if (this.minimumAmount && Math.round(this.toPay.value * 100) < this.minimumAmount) {
      this.toPay.setErrors({
        min: true
      });
      return;
    }

    this.setWorking(true);
    this.intentRequest(Math.round(this.toPay.value * 100))
      .pipe(first())
      .subscribe({
        next: (result: CreateIntentResponse) => {
          console.log(result);
          this.setWorking(false);
          this.modalCtrl.dismiss({
            intentResponse: result
          });
        },
        error: (response: HttpErrorResponse) => {
          this.setWorking(false);
          console.log(response);
          if (response.status === 0) {
            this.setIntentError(`It looks like you're offline. Please check your connection and try again.`);
          } else {
            switch (response?.error?.ErrorCode) {
              case 2000:
                this.setIntentError('Request is now closed');
                break;
              case 2001:
                this.setIntentError(
                  'This request is no longer active for your child. Please contact the school for more information.'
                );
                break;
              case 2002:
                this.setIntentError(
                  `You're unable to pay right now as another contact is viewing this request. Please try again in a couple of minutes.`
                );
                break;
              case 2003:
                this.setIntentError('Error: SchoolSubscriberError');
                break;
              case 2004:
                this.setIntentError('REQUEST IS FULLY PAID');
                break;
              case 2005:
                this.setIntentError('Invalid balance');
                break;
              case 2006:
                this.setIntentError('Please enter at least the minimum amount.');
                break;
              default:
                this.setIntentError('Something went wrong. Please try again later');
            }
          }
        }
      });
  }

  get formattedBalance(): string {
    return (this.balance / 100.0).toFixed(2);
  }
  get balancePounds(): string {
    return this.formattedBalance.split('.')[0];
  }
  get balancePence(): string {
    return this.formattedBalance.split('.')[1];
  }
  get formattedMinimum(): string {
    return (this.minimumAmount / 100.0).toFixed(2);
  }
  get toPay(): UntypedFormControl {
    return this.form.get('toPay') as UntypedFormControl;
  }

  // Remaining amount should not be less than balance - £1
  RemainingAmountValidator(balance: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value: number = Math.round(+control.value * 100);
      if (value >= balance) return;
      if (value > balance - 100) {
        return { remainingAmount: true };
      }
      return null;
    };
  }
}
